import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { EntryCard } from "@/components/Entries/EntryCard";
import { EntriesSectionHeader } from "@/components/Entries/EntriesSectionHeader";
import { EntrySpotlight } from "@/components/Entries/EntrySpotlight";
import { WelcomeSection } from "@/components/Entries/WelcomeSection";
import { EmptyState } from "@/components/Entries/EmptyState";
import { NoSearchResults } from "@/components/Entries/NoSearchResults";
import { EntryList } from "@/components/Entries/EntryList";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"; 
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";

// Define types
type Entry = Database["public"]["Tables"]["entries"]["Row"] & {
  creator_email?: string;
  last_editor_email?: string;
};

type DashboardStats = {
  total: number;
  favorites: number;
  recentUpdates: number;
  lastEditedDate: string;
};

// Helper function to strip HTML tags
const stripHtmlTags = (html: string | null) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const Dashboard = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query") || "";
  const categoryFilter = searchParams.get("category") || "all";
  const [stats, setStats] = useState<DashboardStats>({
    total: 0,
    favorites: 0,
    recentUpdates: 0,
    lastEditedDate: "",
  });

  useEffect(() => {
    if (user) {
      fetchEntries();
    }
  }, [user, categoryFilter]);

  const fetchEntries = async () => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      let query = supabase
        .from("entries")
        .select("*")
        .order("updated_at", { ascending: false });
      
      // Apply category filter if it's not "all"
      if (categoryFilter !== "all") {
        query = query.eq("entry_type", categoryFilter as Database["public"]["Enums"]["entry_type"]);
      }
      
      const { data, error } = await query;
  
      if (error) {
        console.error("Error fetching entries:", error);
        setLoading(false);
        return;
      }
      
      const entriesWithEmail = data?.map(entry => {
        let creatorEmail = "Unknown";
        if (user.id === entry.creator_id) {
          creatorEmail = user.email || "Unknown";
        }
        
        return {
          ...entry,
          creator_email: creatorEmail,
        };
      }) || [];
      
      setEntries(entriesWithEmail);
      
      // Calculate stats
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      setStats({
        total: entriesWithEmail.length,
        favorites: entriesWithEmail.filter(e => e.is_favorite).length,
        recentUpdates: entriesWithEmail.filter(e => 
          new Date(e.updated_at) > oneWeekAgo
        ).length,
        lastEditedDate: entriesWithEmail.length > 0 ? 
          new Date(entriesWithEmail[0].updated_at).toLocaleDateString() : 
          "No entries yet",
      });
    } catch (error) {
      console.error("Error in fetchEntries:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter entries based on search query
  const filteredEntries = useMemo(() => {
    if (!searchQuery) return entries;
    
    return entries.filter(entry => 
      entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (entry.entry_type && entry.entry_type.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (entry.content && stripHtmlTags(entry.content).toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [entries, searchQuery]);

  // Group entries by categories
  const pinnedEntries = useMemo(() => 
    filteredEntries.filter(entry => entry.is_pinned && (!entry.password)),
    [filteredEntries]
  );
  
  const favoriteEntries = useMemo(() => 
    filteredEntries.filter(entry => entry.is_favorite && !entry.is_pinned && (!entry.password)),
    [filteredEntries]
  );
  
  const recentEntries = useMemo(() => 
    filteredEntries.filter(entry => !entry.is_favorite && !entry.is_pinned),
    [filteredEntries]
  );

  // Get a random entry for the spotlight that is not password protected
  const spotlightEntry = useMemo(() => {
    if (entries.length === 0) return null;
    const nonPasswordProtectedEntries = entries.filter(entry => !entry.password);
    if (nonPasswordProtectedEntries.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * nonPasswordProtectedEntries.length);
    return nonPasswordProtectedEntries[randomIndex];
  }, [entries]);

  // Define the active category name
  const activeCategoryName = useMemo(() => {
    if (categoryFilter === "all") return "All Entries";
    return categoryFilter;
  }, [categoryFilter]);

  // Handle the toggle of favorite status for entries
  const toggleFavorite = async (entry: Entry) => {
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_favorite: !entry.is_favorite })
        .eq("id", entry.id);
  
      if (!error) {
        setEntries(entries.map(e => 
          e.id === entry.id ? { ...e, is_favorite: !e.is_favorite } : e
        ));
      } else {
        console.error("Error toggling favorite:", error);
      }
    } catch (error) {
      console.error("Error in toggleFavorite:", error);
    }
  };

  // Handle the toggle of pinned status for entries
  const togglePinned = async (entry: Entry) => {
    const isPinned = entry.is_pinned || false;
    
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_pinned: !isPinned })
        .eq("id", entry.id);
  
      if (!error) {
        setEntries(entries.map(e => 
          e.id === entry.id ? { ...e, is_pinned: !isPinned } : e
        ));
      } else {
        console.error("Error toggling pinned status:", error);
      }
    } catch (error) {
      console.error("Error in togglePinned:", error);
    }
  };

  // Copy share link
  const copyShareLink = (entry: Entry) => {
    const shareUrl = `${window.location.origin}/shared/${entry.share_id}`;
    navigator.clipboard.writeText(shareUrl);
  };

  // Handle removing a shared entry
  const handleRemoveSharedEntry = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from("entries")
        .delete()
        .eq("id", entryId);

      if (error) {
        console.error("Error removing shared entry:", error);
        return;
      }

      // Remove the entry from the local state
      setEntries(entries.filter(e => e.id !== entryId));
      toast.success("Entry removed from your dashboard");
    } catch (error) {
      console.error("Error in handleRemoveSharedEntry:", error);
      toast.error("Failed to remove entry");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Your Knowledge Base</h1>
        {/* Loading placeholders */}
        {/* ... keep existing code for loading state */}
      </div>
    );
  }

  // Render "No entries found" message when search has no results
  if (filteredEntries.length === 0 && searchQuery) {
    return <NoSearchResults searchQuery={searchQuery} />;
  }

  return (
    <div>
      {/* Dashboard header with category name */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          {activeCategoryName}
          {categoryFilter !== "all" && (
            <span className="ml-2 text-sm text-muted-foreground">
              ({filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'})
            </span>
          )}
        </h1>
        <Link to="/dashboard/entry/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New Entry
          </Button>
        </Link>
      </div>

      {entries.length === 0 && !searchQuery ? (
        <EmptyState />
      ) : (
        <div className="space-y-8">
          {/* Welcome section with metrics */}
          <WelcomeSection user={user} stats={stats} />

          {/* Pinned entries section */}
          {pinnedEntries.length > 0 && (
            <div className="mb-8">
              <EntriesSectionHeader icon="pin" title="Pinned Entries" />
              <EntryList 
                entries={pinnedEntries}
                currentUserId={user?.id}
                toggleFavorite={toggleFavorite}
                togglePinned={togglePinned}
                copyShareLink={copyShareLink}
                stripHtmlTags={stripHtmlTags}
                onRemoveSharedEntry={handleRemoveSharedEntry}
              />
            </div>
          )}
          
          {/* Recently Updated Section (horizontal scroll) */}
          {recentEntries.length > 0 && (
            <div className="mb-8">
              <EntriesSectionHeader title="Recently Updated" />
              <ScrollArea className="whitespace-nowrap pb-4">
                <div className="flex space-x-4">
                  {recentEntries.slice(0, Math.min(8, recentEntries.length)).map((entry) => (
                    <Card key={entry.id} className="w-[300px] flex-shrink-0">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <Link to={`/dashboard/entry/${entry.id}`}>
                            <CardTitle className="text-lg hover:text-blue-600 hover:underline transition-colors line-clamp-1">
                              {entry.title}
                            </CardTitle>
                          </Link>
                        </div>
                        <div className="flex flex-wrap gap-1 my-1">
                          <Badge variant="outline" className="text-xs">
                            {entry.entry_type}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(entry.updated_at).toLocaleDateString()}
                        </div>
                      </CardHeader>
                      <CardContent className="h-16 overflow-hidden">
                        {!entry.password && (
                          <p className="text-sm line-clamp-3">
                            {stripHtmlTags(entry.content) || "No content"}
                          </p>
                        )}
                        {entry.password && (
                          <p className="text-sm italic text-muted-foreground">
                            Password protected content
                          </p>
                        )}
                      </CardContent>
                      <CardFooter className="border-t pt-3">
                        <Button variant="outline" size="sm" asChild className="w-full">
                          <Link to={`/dashboard/entry/${entry.id}`}>View</Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>
          )}
          
          {/* Favorites section */}
          {favoriteEntries.length > 0 && (
            <div className="mb-8">
              <EntriesSectionHeader icon="star" title="Favorites" />
              <EntryList 
                entries={favoriteEntries}
                currentUserId={user?.id}
                toggleFavorite={toggleFavorite}
                togglePinned={togglePinned}
                copyShareLink={copyShareLink}
                stripHtmlTags={stripHtmlTags}
              />
            </div>
          )}
          
          {/* All entries or filtered entries */}
          {recentEntries.length > 0 && (favoriteEntries.length > 0 || pinnedEntries.length > 0) && (
            <div>
              <EntriesSectionHeader title="All Entries" />
              <EntryList 
                entries={recentEntries}
                currentUserId={user?.id}
                toggleFavorite={toggleFavorite}
                togglePinned={togglePinned}
                copyShareLink={copyShareLink}
                stripHtmlTags={stripHtmlTags}
              />
            </div>
          )}
          
          {/* Entry Spotlight */}
          {spotlightEntry && entries.length > 3 && !spotlightEntry.password && (
            <EntrySpotlight entry={spotlightEntry} stripHtmlTags={stripHtmlTags} />
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
